import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { markets } from "../data/markets";
import { services } from "../data/services";
import { PageHead } from "../components/PageHead";
import { Reveal } from "../components/Reveal";
import { useTitle } from "../lib/useTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_OPTIONS = [...markets.map((m) => m.name), "Another country"];
const SERVICE_OPTIONS = [...services.map((s) => s.name), "Not sure yet"];

const INITIAL = { name: "", email: "", company: "", service: "Not sure yet", countries: [], message: "", consent: false };

const inputClass =
  "w-full rounded-lg border border-line bg-base px-4 py-3 text-sm text-ink placeholder:text-mute/70 transition-colors duration-200 focus:border-coral focus:outline-none";

const Field = ({ label, htmlFor, required = false, error, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink">
      {label}
      {required && <span className="ml-0.5 text-coral">*</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1.5 text-xs text-coral" role="alert">
        {error}
      </p>
    )}
  </div>
);

export default function Contact() {
  useTitle("Contact | Amplified Minds");
  const [params] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const service = params.get("service");
    const market = params.get("market");
    setForm((f) => ({
      ...f,
      service: service && SERVICE_OPTIONS.includes(service) ? service : f.service,
      countries:
        market && COUNTRY_OPTIONS.includes(market) && !f.countries.includes(market)
          ? [...f.countries, market]
          : f.countries,
    }));
  }, [params]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const toggleCountry = (name) => {
    setForm((f) => ({
      ...f,
      countries: f.countries.includes(name) ? f.countries.filter((c) => c !== name) : [...f.countries, name],
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = "Please enter a valid email";
    if (!form.message.trim()) next.message = "Please tell us briefly about your enquiry";
    if (!form.consent) next.consent = "Consent is required to submit";
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          service_line: form.service,
          markets: form.countries,
          message: form.message.trim(),
          consent: form.consent,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setSent(true);
      toast.success("Your enquiry has been received.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHead
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
        title="Tell us what you want to build, and where."
        lede="Choose a service and the countries you have in mind. We reply with a first view on structure and next steps."
      >
        <div className="mt-7 space-y-2 text-sm text-mute" data-testid="contact-phones">
          <p>
            <span className="font-semibold text-ink">UAE</span> +971 50 571 4162
          </p>
          <p>
            <span className="font-semibold text-ink">India</span> +91 74510 61965 · +91 98739 65559
          </p>
        </div>
      </PageHead>

      <section className="py-16 md:py-24">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mx-auto max-w-3xl rounded-[20px] border border-line bg-surface p-6 shadow-surface sm:p-9">
              {sent ? (
                <div className="flex flex-col items-start gap-4 py-8" data-testid="contact-success">
                  <CheckCircle2 className="h-10 w-10 text-emerald" aria-hidden="true" />
                  <h2 className="font-display text-2xl font-bold text-ink">
                    Thank you. We have your enquiry and will reply soon.
                  </h2>
                  <button
                    type="button"
                    data-testid="contact-send-another"
                    onClick={() => {
                      setSent(false);
                      setForm(INITIAL);
                    }}
                    className="text-sm font-semibold text-coral"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Your name" htmlFor="contact-name" required error={errors.name}>
                      <input id="contact-name" data-testid="contact-name-input" type="text" value={form.name} onChange={set("name")} className={inputClass} autoComplete="name" />
                    </Field>
                    <Field label="Email" htmlFor="contact-email" required error={errors.email}>
                      <input id="contact-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className={inputClass} autoComplete="email" />
                    </Field>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Company or family" htmlFor="contact-company">
                      <input id="contact-company" data-testid="contact-company-input" type="text" value={form.company} onChange={set("company")} className={inputClass} autoComplete="organization" />
                    </Field>
                    <Field label="Service" htmlFor="contact-service">
                      <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputClass}>
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-medium text-ink">Countries</p>
                    <div className="flex flex-wrap gap-2" data-testid="contact-countries">
                      {COUNTRY_OPTIONS.map((c) => {
                        const on = form.countries.includes(c);
                        return (
                          <button
                            key={c}
                            type="button"
                            aria-pressed={on}
                            data-testid={`contact-country-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                            onClick={() => toggleCountry(c)}
                            className="rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200"
                            style={
                              on
                                ? { background: "var(--ink)", borderColor: "var(--ink)", color: "var(--on-accent)" }
                                : { background: "var(--surface)", borderColor: "var(--line)", color: "var(--muted)" }
                            }
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <Field label="What do you need?" htmlFor="contact-message" required error={errors.message}>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      className={`${inputClass} resize-y`}
                    />
                  </Field>
                  <Field label="" htmlFor="contact-consent" error={errors.consent}>
                    <label htmlFor="contact-consent" className="flex cursor-pointer items-start gap-3 text-sm text-mute">
                      <input id="contact-consent" data-testid="contact-consent-checkbox" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 rounded border-line accent-coral" />
                      I agree to be contacted about my enquiry.
                    </label>
                  </Field>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={sending}
                    className="am-btn w-full px-7 py-3.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send enquiry"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
